import { set } from "react-ga";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { CurrencyE, PaymentTypeE, PurchaseStepE } from "../models/enums";
import {
  calculatePurchasePrice,
  createTransaction,
  downgradeAddon,
  downgradeSubscription,
  fetchActiveJMPlans,
  fetchAddonData,
  fetchCountries,
  validateCouponCode,
} from "./purchasesThunks";
import type {
  ActiveJMPlanT,
  BillingDataT,
  CountryT,
  CreditCardT,
  DowngradeDetailsT,
  PackageTypeRouteT,
  TransactionT,
} from "../models/models";

const INITIAL_COMPLETED_STEP_NUMBER = 0;
const INITIAL_ADDON_QUANTITY = 1;
const ZERO_PRICE = 0;

interface CouponCodeI {
  code?: string;
  isValid?: boolean;
  validationText?: string;
  discount?: number;
  expireDate?: string;
  isForMonthlyPeriodOnly?: boolean;
  status: "not-validated" | "validating" | "validated";
}

interface AddonI {
  FeatureSystemName: Pick<PackageTypeRouteT, "sub-users" | "extra-storage">;
  Title: string;
  EUR: number;
  ILS: number;
  USD: number;
}

interface InitialStateI {
  countries: CountryT[];
  activeJMPlans: ActiveJMPlanT[];
  currency: CurrencyE;
  couponCode: CouponCodeI; // Applies for subscription plans only
  creditCards: CreditCardT[];
  selectedCreditCardId?: number;
  isCreditCardsLoading: boolean;
  newCreditCardIframeUrl?: string;
  paymentType?: PaymentTypeE;
  completedStep: PurchaseStepE | 0;
  addon?: AddonI;
  addonQuantity: number;
  transactionDetails?: TransactionT;
  downgradeDetails?: DowngradeDetailsT;
  purchasePrice: number;
  isFetchingPurchasePrice: boolean;
  billingData?: BillingDataT;
  isPurchaseFailed?: boolean;
  isFetchingAddonData: boolean;
  isTransactionExecuting?: boolean;
  isFetchingActiveJMPlans?: boolean;
  jumboCodeQuantity?: number;
  newCreditCardIframeUrlError?: string;
  isSubscriptionPurchase: boolean;

  // Need to display "Manage sub users" link
  // in case downgrade sub users amount is not allowed.
  downgradeAddonErrorMessage?: string;
}

const couponCodeInitialState: CouponCodeI = {
  status: "not-validated",
};

const initialState: InitialStateI = {
  countries: [],
  activeJMPlans: [],
  currency: CurrencyE.ILS,
  couponCode: couponCodeInitialState,
  creditCards: [],
  isCreditCardsLoading: true,
  paymentType: PaymentTypeE.CREDIT_CARD,
  completedStep: INITIAL_COMPLETED_STEP_NUMBER,
  addonQuantity: INITIAL_ADDON_QUANTITY,
  purchasePrice: ZERO_PRICE,
  isFetchingAddonData: true,
  isFetchingPurchasePrice: true,
  isTransactionExecuting: false,
  isFetchingActiveJMPlans: true,
  isSubscriptionPurchase: true,
};

const purchasesSlice = createSlice({
  name: "purchases",
  initialState,
  reducers: {
    setCurrency: (state, action: PayloadAction<CurrencyE>) => {
      state.currency = action.payload;
    },

    // ============================== Coupon Code ==============================

    setCouponCode: (state, action: PayloadAction<string>) => {
      state.couponCode.code = action.payload;
    },
    setCouponCodeIsValid: (
      state,
      action: PayloadAction<{ isValid: boolean; validationText?: string }>
    ) => {
      state.couponCode.isValid = action.payload.isValid;
      state.couponCode.validationText = action.payload.validationText;
    },
    setCouponCodeExpireDate: (state, action: PayloadAction<string>) => {
      state.couponCode.expireDate = action.payload;
    },
    setCouponCodeDiscount: (state, action: PayloadAction<number>) => {
      state.couponCode.discount = action.payload;
    },
    clearCouponCode: (state) => {
      state.couponCode = couponCodeInitialState;
    },
    setIsForMonthlyPeriodOnly: (state, action: PayloadAction<boolean>) => {
      state.couponCode.isForMonthlyPeriodOnly = action.payload;
    },

    // ============================  Payment =================================

    setCreditCards: (state, action: PayloadAction<CreditCardT[]>) => {
      state.creditCards = action.payload;
    },
    setSelectedCreditCardId: (state, action: PayloadAction<number>) => {
      state.selectedCreditCardId = action.payload;
    },
    setIsCreditCardsLoading: (state, action: PayloadAction<boolean>) => {
      state.isCreditCardsLoading = action.payload;
    },
    setNewCreditCardIframeUrl: (state, action: PayloadAction<string>) => {
      state.newCreditCardIframeUrl = action.payload;
    },
    setPaymentType: (state, action: PayloadAction<PaymentTypeE>) => {
      state.paymentType = action.payload;
    },
    setCompletedStep: (state, action: PayloadAction<PurchaseStepE | 0>) => {
      state.completedStep = action.payload;
    },
    setTransactionDetails: (state, action: PayloadAction<TransactionT>) => {
      state.transactionDetails = action.payload;
    },
    setDowngradeDetails: (state, action: PayloadAction<DowngradeDetailsT>) => {
      state.downgradeDetails = action.payload;
    },
    setPurchasePrice: (state, action: PayloadAction<number>) => {
      state.purchasePrice = action.payload;
    },
    setBillingData: (state, action: PayloadAction<BillingDataT>) => {
      state.billingData = action.payload;
    },
    setIsPurchaseFailed: (state, action: PayloadAction<boolean>) => {
      state.isPurchaseFailed = action.payload;
    },

    // ============================  Addons =================================

    setAddonData: (state, action: PayloadAction<AddonI>) => {
      state.addon = action.payload;
    },
    setAddonQuantity: (state, action: PayloadAction<number>) => {
      state.addonQuantity = action.payload;
    },
    setDowngradeAddonErrorMessage: (state, action: PayloadAction<string>) => {
      state.downgradeAddonErrorMessage = action.payload;
    },

    // ======================================================================

    setIsTransactionExecuting: (state, action: PayloadAction<boolean>) => {
      state.isTransactionExecuting = action.payload;
    },
    setJumboCodeQuantity: (state, action: PayloadAction<number>) => {
      state.jumboCodeQuantity = action.payload;
    },
    setNewCreditCardIframeUrlError: (state, action: PayloadAction<string>) => {
      state.newCreditCardIframeUrlError = action.payload;
    },
    setIsSubscriptionPurchase: (state, action: PayloadAction<boolean>) => {
      state.isSubscriptionPurchase = action.payload;
    },
  },
  // Handle async actions
  extraReducers(builder) {
    builder.addCase(
      fetchCountries.fulfilled,
      (state, action: PayloadAction<CountryT[]>) => {
        state.countries = action.payload;
      }
    );
    builder.addCase(createTransaction.pending, (state) => {
      state.isTransactionExecuting = true;
    });
    builder.addCase(createTransaction.fulfilled, (state) => {
      state.isTransactionExecuting = false;
    });
    builder.addCase(createTransaction.rejected, (state) => {
      state.isTransactionExecuting = false;
    });
    builder.addCase(downgradeSubscription.fulfilled, (state) => {
      state.isTransactionExecuting = false;
    });
    builder.addCase(downgradeSubscription.pending, (state) => {
      state.isTransactionExecuting = true;
    });
    builder.addCase(downgradeSubscription.rejected, (state) => {
      state.isTransactionExecuting = false;
    });
    builder.addCase(downgradeAddon.fulfilled, (state) => {
      state.isTransactionExecuting = false;
    });
    builder.addCase(downgradeAddon.pending, (state) => {
      state.isTransactionExecuting = true;
    });
    builder.addCase(downgradeAddon.rejected, (state) => {
      state.isTransactionExecuting = false;
    });
    builder.addCase(fetchActiveJMPlans.fulfilled, (state, action) => {
      state.activeJMPlans = action.payload;
      state.isFetchingActiveJMPlans = false;
    });
    builder.addCase(fetchAddonData.fulfilled, (state) => {
      state.isFetchingAddonData = false;
    });
    builder.addCase(fetchAddonData.rejected, (state) => {
      state.isFetchingAddonData = false;
    });
    builder.addCase(validateCouponCode.pending, (state) => {
      state.couponCode.status = "validating";
    });
    builder.addCase(validateCouponCode.fulfilled, (state) => {
      state.couponCode.status = "validated";
    });
    // It doesn't mean code is valid.
    // It just shows the validation process was executed.
    builder.addCase(validateCouponCode.rejected, (state) => {
      state.couponCode.status = "validated";
    });
    builder.addCase(calculatePurchasePrice.fulfilled, (state) => {
      state.isFetchingPurchasePrice = false;
    });
  },
});

export const {
  setCurrency,
  setCouponCodeDiscount,
  setCouponCodeExpireDate,
  setCouponCodeIsValid,
  setCouponCode,
  clearCouponCode,
  setCreditCards,
  setSelectedCreditCardId,
  setPaymentType,
  setCompletedStep,
  setIsCreditCardsLoading,
  setAddonData,
  setAddonQuantity,
  setDowngradeAddonErrorMessage,
  setTransactionDetails,
  setDowngradeDetails,
  setNewCreditCardIframeUrl,
  setPurchasePrice,
  setBillingData,
  setIsPurchaseFailed,
  setIsTransactionExecuting,
  setJumboCodeQuantity,
  setIsForMonthlyPeriodOnly,
  setNewCreditCardIframeUrlError,
  setIsSubscriptionPurchase,
} = purchasesSlice.actions;

export default purchasesSlice.reducer;
